import React from "react";
import { Button } from "../../../../../ui/components/Button";
import { TextField } from "../../../../../ui/components/TextField";
import { FieldFootnote } from "./FieldFootnote";
import { FieldRowProps } from "./fieldTypes";

/**
 * ZOUR-19 — a calendar date, stored and sent as `YYYY-MM-DD`.
 *
 * A native `<input type="date">` rather than the app's Calendar popover: the
 * value here is a plain day with no time and no range, the browser's own picker
 * already speaks `YYYY-MM-DD` in `value`, and the server validates that exact
 * format. Nothing in between to get wrong.
 *
 * `suggestion` is what makes this row worth its own component. The GST date has
 * a value Zoho Books may already know, and offering it as one click is the
 * difference between a date that matches Zoho and a date somebody retyped from
 * memory — the mismatch the ZOUR-19 research calls the biggest operational risk.
 * The button appears only when there is a suggestion and it is not already the
 * value.
 */
export const DateFieldRow: React.FC<FieldRowProps> = ({
  config,
  value,
  locked,
  lockReason,
  errorMessage,
  suggestion,
  suggestionLabel,
  onChange,
}) => {
  const canSuggest = !locked && Boolean(suggestion) && suggestion !== value;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full items-end gap-2">
        <TextField
          className="w-full"
          label={config.label}
          disabled={locked}
          error={Boolean(errorMessage)}
        >
          <TextField.Input
            type="date"
            value={value}
            disabled={locked}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          />
        </TextField>

        {canSuggest && (
          <Button
            variant="neutral-secondary"
            onClick={() => onChange(suggestion as string)}
          >
            {suggestionLabel ?? "Use suggested date"}
          </Button>
        )}
      </div>

      <FieldFootnote
        hint={config.hint}
        example={config.example}
        lockReason={lockReason}
        errorMessage={errorMessage}
      />
    </div>
  );
};
